import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Sparkles, Copy, Loader2, BarChart3, TrendingUp, Instagram, Youtube, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const SOURCE_COLORS = {
  instagram: '#E4405F',
  youtube: '#FF0000',
  whatsapp: '#25D366',
  facebook: '#1877F2',
  google: '#4285F4',
  direct: '#94A3B8',
};

const platformOptions = [
  { value: 'instagram_reel', label: 'Instagram Reel (30s)' },
  { value: 'youtube_short', label: 'YouTube Short (60s)' },
  { value: 'youtube_long', label: 'YouTube Long-form (5-7min)' },
];

const langOptions = [
  { value: 'hinglish', label: 'Hinglish' },
  { value: 'hindi', label: 'Hindi (Devanagari)' },
  { value: 'english', label: 'English' },
];

const topicIdeas = [
  'EMI shock: ₹50L home loan in 2026 vs 2021',
  'HDFC Millennia vs Axis Magnus — best for ₹1L/mo spender',
  '5 mistakes that are tanking your CIBIL score',
  'Old vs New tax regime — which saves you more?',
  'Personal loan vs Credit card EMI — when to use what',
  'Why your home loan got rejected (and how to fix it)',
  'How to get a credit card with no credit history',
  'Top 3 SIP mistakes Indians make under 30',
];

const MarketingTab = () => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` };
  const [sources, setSources] = useState(null);
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('instagram_reel');
  const [language, setLanguage] = useState('hinglish');
  const [ctaService, setCtaService] = useState('Home Loan');
  const [generating, setGenerating] = useState(false);
  const [script, setScript] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/marketing/sources`, { headers })
      .then((r) => setSources(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildCtaUrl = () => {
    const base = window.location.origin;
    const params = new URLSearchParams({
      service: ctaService,
      utm_source: platform.startsWith('instagram') ? 'instagram' : 'youtube',
      utm_medium: platform === 'instagram_reel' ? 'reel' : platform === 'youtube_short' ? 'short' : 'video',
      utm_campaign: topic.slice(0, 40).replace(/\s+/g, '_').toLowerCase() || 'general',
    });
    return `${base}/enquiry?${params.toString()}`;
  };

  const generate = async () => {
    if (!topic.trim()) { toast.error('Please enter a topic'); return; }
    setGenerating(true);
    setScript(null);
    try {
      const res = await axios.post(`${API}/admin/marketing/generate-script`, {
        topic, platform, language, cta_url: buildCtaUrl(),
      }, { headers });
      setScript(res.data);
      toast.success('Script ready!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed. Check Universal Key balance.');
    } finally {
      setGenerating(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const pieData = (sources?.by_source || []).map((s) => ({
    name: s.source || 'direct',
    value: s.count,
    fill: SOURCE_COLORS[(s.source || 'direct').toLowerCase()] || '#FF6B35',
  }));

  return (
    <div className="fade-in" data-testid="admin-marketing-section">
      {/* Source breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
        <div className="calculator-section" style={{ padding: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
            <BarChart3 size={18} style={{ color: '#FF6B35' }} />
            <h3 style={{ fontSize: '1.05rem', color: '#0F172A', margin: 0 }}>Leads by Traffic Source</h3>
          </div>
          {pieData.length === 0 || pieData.every(p => p.value === 0) ? (
            <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#94A3B8', fontSize: '0.9rem' }}>
              No tagged leads yet. Add <code style={{ background: '#F1F5F9', padding: '2px 6px', borderRadius: 4 }}>?utm_source=instagram</code> to your reel links.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={70} label={(d) => `${d.name} (${d.value})`}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="calculator-section" style={{ padding: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
            <TrendingUp size={18} style={{ color: '#FF6B35' }} />
            <h3 style={{ fontSize: '1.05rem', color: '#0F172A', margin: 0 }}>Top Campaigns</h3>
          </div>
          {sources?.by_campaign?.length ? (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {sources.by_campaign.slice(0, 6).map((c, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: '#F8FAFC', borderRadius: 8 }} data-testid={`campaign-row-${i}`}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#0F172A', fontSize: '0.9rem' }}>{c.campaign}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748B' }}>via {c.source}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, color: '#FF6B35' }}>{c.count}</div>
                    <div style={{ fontSize: '0.7rem', color: '#16A34A' }}>{c.converted} converted</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#94A3B8', fontSize: '0.9rem' }}>
              No campaigns yet. Add <code style={{ background: '#F1F5F9', padding: '2px 6px', borderRadius: 4 }}>?utm_campaign=cc_battle_aug</code> to track ROI per video.
            </div>
          )}
        </div>
      </div>

      {/* Script Generator */}
      <div className="calculator-section" style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem' }}>
          <Sparkles size={20} style={{ color: '#FF6B35' }} />
          <h3 style={{ fontSize: '1.15rem', color: '#0F172A', margin: 0 }}>AI Reel & Video Script Generator</h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
          <div>
            <label className="label-text">Topic</label>
            <input
              className="input-field"
              placeholder="e.g., HDFC Millennia vs Axis Magnus"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              data-testid="script-topic-input"
            />
          </div>
          <div>
            <label className="label-text">Platform</label>
            <select className="input-field" value={platform} onChange={(e) => setPlatform(e.target.value)} data-testid="script-platform-select">
              {platformOptions.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-text">Language</label>
            <select className="input-field" value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="script-language-select">
              {langOptions.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-text">CTA service</label>
            <select className="input-field" value={ctaService} onChange={(e) => setCtaService(e.target.value)} data-testid="script-cta-select">
              {['Home Loan', 'Personal Loan', 'Car Loan', 'Education Loan', 'Business Loan', 'Credit Card', 'Insurance'].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '1.25rem', fontSize: '0.825rem', color: '#64748B' }}>
          Quick ideas:
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            {topicIdeas.map((t) => (
              <button key={t} onClick={() => setTopic(t)} style={{ background: '#F1F5F9', border: '1px solid #E2E8F0', color: '#475569', padding: '4px 10px', borderRadius: 999, fontSize: '0.75rem', cursor: 'pointer' }} data-testid="topic-idea-chip">
                {t}
              </button>
            ))}
          </div>
        </div>

        <button onClick={generate} disabled={generating} className="primary-button" style={{ width: '100%', justifyContent: 'center' }} data-testid="generate-script-button">
          {generating ? <><Loader2 size={18} className="animate-spin" /> Generating with Gemini...</> : <><Sparkles size={18} /> Generate Script</>}
        </button>
      </div>

      {script && (
        <div className="calculator-section" style={{ padding: '1.5rem', marginTop: '1.25rem' }} data-testid="generated-script-output">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{script.platform}</div>
              <h3 style={{ fontSize: '1.4rem', color: '#0F172A', margin: '0.25rem 0 0' }}>{script.title || script.topic}</h3>
            </div>
            <a href={script.cta_url} target="_blank" rel="noopener noreferrer" className="outline-button" style={{ fontSize: '0.8rem' }} data-testid="preview-cta-link">
              <ExternalLink size={14} /> Preview CTA link
            </a>
          </div>

          {script.hook && (
            <div style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)', padding: '1rem', borderRadius: 12, marginBottom: '1rem', borderLeft: '4px solid #FF6B35' }}>
              <div style={{ fontSize: '0.7rem', color: '#9A3412', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>Hook (first 2 seconds)</div>
              <div style={{ fontSize: '1.05rem', color: '#0F172A', marginTop: 4, fontWeight: 600 }}>{script.hook}</div>
            </div>
          )}

          <ScriptSection title="Full Script" content={script.script} onCopy={() => copy(script.script, 'Script')} testId="script-full" />
          <ScriptSection title="Caption (paste on Instagram/YouTube)" content={script.captions} onCopy={() => copy(script.captions, 'Caption')} testId="script-caption" />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem', marginTop: '1rem' }}>
            {script.cta && (
              <InfoBox label="Spoken CTA" value={script.cta} />
            )}
            {script.thumbnail_idea && (
              <InfoBox label="Thumbnail Idea" value={script.thumbnail_idea} />
            )}
            {script.best_post_time_ist && (
              <InfoBox label="Best Post Time (IST)" value={script.best_post_time_ist} />
            )}
          </div>

          {script.hashtags && script.hashtags.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Hashtags</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {script.hashtags.map((h, i) => (
                  <span key={i} style={{ background: '#F1F5F9', color: '#1E40AF', padding: '4px 10px', borderRadius: 999, fontSize: '0.8rem', fontWeight: 600 }}>{h}</span>
                ))}
              </div>
              <button onClick={() => copy(script.hashtags.join(' '), 'Hashtags')} className="outline-button" style={{ marginTop: 8, fontSize: '0.75rem', padding: '0.4rem 0.8rem' }} data-testid="copy-hashtags-button">
                <Copy size={12} /> Copy hashtags
              </button>
            </div>
          )}

          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#F0F9FF', borderRadius: 12, border: '1px dashed #93C5FD' }}>
            <div style={{ fontSize: '0.825rem', color: '#1E3A8A', marginBottom: 6, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Instagram size={14} /> <Youtube size={14} /> Trackable CTA link (paste in bio / description)
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <code style={{ flex: 1, padding: '0.5rem 0.75rem', background: 'white', borderRadius: 8, border: '1px solid #BFDBFE', fontSize: '0.8rem', overflow: 'auto', whiteSpace: 'nowrap' }}>{script.cta_url}</code>
              <button onClick={() => copy(script.cta_url, 'CTA URL')} className="primary-button" style={{ fontSize: '0.75rem', padding: '0.5rem 0.8rem' }} data-testid="copy-cta-url-button">
                <Copy size={12} /> Copy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ScriptSection = ({ title, content, onCopy, testId }) => (
  <div style={{ marginBottom: '1rem' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
      <div style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>{title}</div>
      <button onClick={onCopy} className="outline-button" style={{ fontSize: '0.75rem', padding: '0.3rem 0.7rem' }} data-testid={`copy-${testId}`}>
        <Copy size={12} /> Copy
      </button>
    </div>
    <pre style={{ background: '#0F172A', color: '#F1F5F9', padding: '1rem', borderRadius: 10, fontSize: '0.85rem', overflow: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.6, fontFamily: 'inherit', margin: 0 }} data-testid={testId}>
      {content || '—'}
    </pre>
  </div>
);

const InfoBox = ({ label, value }) => (
  <div style={{ background: '#F8FAFC', padding: '0.75rem 1rem', borderRadius: 10, border: '1px solid #E2E8F0' }}>
    <div style={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>{label}</div>
    <div style={{ fontSize: '0.9rem', color: '#0F172A', marginTop: 4 }}>{value}</div>
  </div>
);

export default MarketingTab;
